import { useState, useEffect, useRef } from 'react';

const SLIDES = [
  {
    img: 'https://public.readdy.ai/ai/img_res/649a714e2b7746c9a80420d4373f1f88.jpg',
    label: 'Gulf Coast Residential',
  },
  {
    img: 'https://readdy.ai/api/search-image?query=wide%20angle%20dramatic%20rooftop%20photography%20professional%20roofing%20crew%20installing%20premium%20dark%20slate%20shingles%20on%20luxury%20two-story%20southern%20home%2C%20golden%20morning%20light%2C%20tools%20and%20materials%20organized%20on%20clean%20roof%20deck%2C%20craftsmanship%20pride%2C%20cinematic%20quality%20editorial%20roofing%20photography&width=1920&height=1080&seq=hero_v5_02&orientation=landscape',
    label: 'Expert Installation',
  },
  {
    img: 'https://readdy.ai/api/search-image?query=breathtaking%20aerial%20view%20upscale%20Mississippi%20Gulf%20Coast%20coastal%20neighborhood%20from%20above%2C%20rows%20of%20beautiful%20homes%20with%20rich%20dark%20charcoal%20premium%20asphalt%20shingle%20rooftops%2C%20late%20afternoon%20dramatic%20shadows%2C%20coastal%20landscape%2C%20tree%20lined%20streets%2C%20editorial%20drone%20photography%20quality&width=1920&height=1080&seq=hero_v5_03&orientation=landscape',
    label: 'Aerial Overview',
  },
];

const FORM_URL = 'https://readdy.ai/api/form/d75tlslr5g02gnd69n5g';

interface FormState {
  name: string; email: string; phone: string;
  service: string; address: string;
}
interface SubmitState { status: 'idle' | 'loading' | 'success' | 'error'; message: string; }

export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [form, setForm] = useState<FormState>({ name: '', email: '', phone: '', service: '', address: '' });
  const [submit, setSubmit] = useState<SubmitState>({ status: 'idle', message: '' });
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    intervalRef.current = setInterval(() => setCurrent(p => (p + 1) % SLIDES.length), 7000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  const goTo = (i: number) => {
    setCurrent(i);
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => setCurrent(p => (p + 1) % SLIDES.length), 7000);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.email || !form.phone) return;
    setSubmit({ status: 'loading', message: '' });
    try {
      const body = new URLSearchParams({
        name: form.name, email: form.email, phone: form.phone,
        service: form.service, address: form.address,
      });
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmit({ status: 'success', message: "We'll be in touch within 24 hours." });
        setForm({ name: '', email: '', phone: '', service: '', address: '' });
      } else {
        setSubmit({ status: 'error', message: 'Something went wrong. Please call us directly.' });
      }
    } catch {
      setSubmit({ status: 'error', message: 'Connection error. Call (228) 547-3887.' });
    }
  };

  return (
    <section className="relative w-full min-h-screen flex items-center overflow-hidden">
      {/* Slideshow backgrounds */}
      {SLIDES.map((slide, i) => (
        <div key={i}
          className="absolute inset-0"
          style={{ opacity: i === current ? 1 : 0, transition: 'opacity 1600ms cubic-bezier(0.4,0,0.2,1)' }}>
          <img src={slide.img} alt={slide.label} className="w-full h-full object-cover object-center" />
        </div>
      ))}

      {/* Heavy dark overlay — ensures text is always readable */}
      <div className="absolute inset-0 bg-black/75"></div>
      <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/20"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30"></div>

      {/* Gold bottom accent */}
      <div className="absolute bottom-0 left-0 w-2/3 h-px bg-gradient-to-r from-gold/60 via-gold/20 to-transparent"></div>

      {/* Slide dots */}
      <div className="absolute bottom-8 left-8 md:left-14 flex items-center gap-4 z-20">
        {SLIDES.map((slide, i) => (
          <button key={i} onClick={() => goTo(i)}
            className="flex items-center gap-2.5 group cursor-pointer">
            <span
              className={`block h-px transition-all ${i === current ? 'w-12 bg-gold' : 'w-4 bg-white/30 group-hover:bg-white/50'}`}
              style={{ transitionDuration: '600ms' }}
            ></span>
            {i === current && (
              <span className="text-[10px] text-gold/80 tracking-widest uppercase hidden md:block font-light">{slide.label}</span>
            )}
          </button>
        ))}
      </div>

      {/* Main content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-14 pt-40 pb-28">
        <div className="flex flex-col xl:flex-row items-start xl:items-center gap-12 xl:gap-20">

          {/* LEFT — headline + CTAs */}
          <div className="flex-1 w-full max-w-2xl">
            {/* Eyebrow */}
            <div className={`flex items-center gap-3 mb-8 transition-all duration-1000 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
              style={{ transitionDelay: '200ms' }}>
              <span className="w-10 h-px bg-gold"></span>
              <span className="text-[10px] font-bold tracking-widest uppercase text-gold">
                GAF Certified &bull; Fortification Certified &bull; Licensed &amp; Insured
              </span>
            </div>

            {/* Location tag */}
            <div className={`mb-4 transition-all duration-1000 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
              style={{ transitionDelay: '280ms' }}>
              <span className="inline-flex items-center gap-2 text-white/70 text-sm font-medium tracking-wide">
                <i className="ri-map-pin-2-fill text-gold text-xs"></i>
                Roofing Services Across the Mississippi Gulf Coast
              </span>
            </div>

            {/* Headline */}
            <h1 className={`leading-none mb-8 w-full transition-all duration-1000 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: '350ms' }}>
              <span className="block font-condensed font-bold text-[72px] md:text-[90px] lg:text-[108px] uppercase leading-none tracking-tighter text-white drop-shadow-lg">
                DONE
              </span>
              <div className="flex items-baseline gap-4">
                <span className="block font-condensed font-bold text-[72px] md:text-[90px] lg:text-[108px] uppercase leading-none tracking-tighter text-white drop-shadow-lg">
                  RIGHT.
                </span>
                <div className="w-16 h-px bg-gold/60 hidden md:block mb-4"></div>
              </div>
              <span className="block font-serif italic text-2xl md:text-3xl text-gold mt-2 leading-snug drop-shadow-md">
                Professional Roofing Built to Last.
              </span>
            </h1>

            {/* Sub copy */}
            <p className={`text-white/80 text-base md:text-lg leading-relaxed max-w-lg mb-10 font-light transition-all duration-1000 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
              style={{ transitionDelay: '500ms' }}>
              All Done Roofing LLC delivers premium roofing systems — roof replacement, repairs, and storm damage restoration — built for long-term performance across{' '}
              <strong className="text-white font-semibold">Gulfport, Biloxi, Ocean Springs, Long Beach, Diamondhead</strong>{' '}
              and surrounding Gulf Coast communities.
            </p>

            {/* CTAs */}
            <div className={`flex flex-col sm:flex-row gap-4 mb-12 transition-all duration-1000 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
              style={{ transitionDelay: '650ms' }}>
              <a href="#estimate"
                onClick={e => { e.preventDefault(); document.getElementById('estimate')?.scrollIntoView({ behavior: 'smooth' }); }}
                className="inline-flex items-center justify-center gap-3 bg-gold text-ink font-bold text-[12px] tracking-widest uppercase px-10 py-4 rounded hover:bg-gold-light transition-all cursor-pointer whitespace-nowrap group">
                <i className="ri-calendar-check-line text-sm"></i>
                Request a Free Estimate
                <i className="ri-arrow-right-line opacity-0 group-hover:opacity-100 -ml-1 group-hover:ml-0 transition-all"></i>
              </a>
              <a href="tel:2285473887"
                className="inline-flex items-center justify-center gap-3 border border-white/30 text-white font-semibold text-[12px] tracking-widest uppercase px-10 py-4 rounded hover:border-gold/60 hover:text-gold transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-phone-fill text-gold text-sm"></i>
                (228) 547-3887
              </a>
            </div>

            {/* Trust signals */}
            <div className={`flex flex-wrap gap-x-6 gap-y-2 transition-all duration-1000 ${loaded ? 'opacity-100' : 'opacity-0'}`}
              style={{ transitionDelay: '800ms' }}>
              {['Free Inspections', 'Insurance Claim Help', 'No Hidden Fees', 'Gulf Coast Local'].map(t => (
                <span key={t} className="flex items-center gap-2 text-[11px] font-medium tracking-widest uppercase text-white/60">
                  <span className="w-1.5 h-1.5 rounded-full bg-gold/80"></span>
                  {t}
                </span>
              ))}
            </div>
          </div>

          {/* RIGHT — estimate form */}
          <div className={`w-full xl:w-[390px] shrink-0 transition-all duration-1000 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
            style={{ transitionDelay: '400ms' }}>
            <div className="relative">
              <div className="absolute -inset-px rounded-lg bg-gradient-to-b from-gold/25 via-transparent to-gold/8 pointer-events-none"></div>
              <div className="relative border border-white/12 rounded-lg overflow-hidden bg-black/70 backdrop-blur-xl">
                {/* Form header */}
                <div className="px-6 pt-6 pb-5 border-b border-white/10">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-bold tracking-widest uppercase text-gold/90">No Obligation</span>
                    <div className="flex items-center gap-1">
                      {[1,2,3,4,5].map(s => <i key={s} className="ri-star-fill text-gold text-[10px]"></i>)}
                      <span className="text-[10px] text-white/40 ml-1">5.0 Google</span>
                    </div>
                  </div>
                  <h3 className="font-condensed font-bold text-2xl text-white uppercase tracking-wide leading-tight">
                    Request a Free Estimate
                  </h3>
                  <p className="text-white/50 text-[11px] mt-1 tracking-wide">Fast response · Honest pricing · No pressure</p>
                </div>

                {submit.status === 'success' ? (
                  <div className="px-6 py-12 text-center">
                    <div className="w-14 h-14 flex items-center justify-center border border-gold/40 rounded-full mx-auto mb-5">
                      <i className="ri-checkbox-circle-fill text-gold text-2xl"></i>
                    </div>
                    <p className="font-condensed font-bold text-2xl text-white uppercase mb-2">Request Received</p>
                    <p className="text-white/50 text-sm">{submit.message}</p>
                  </div>
                ) : (
                  <form id="estimate" onSubmit={handleSubmit} data-readdy-form
                    className="px-6 py-5 flex flex-col gap-3.5"
                    action={FORM_URL} method="POST" encType="application/x-www-form-urlencoded">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex flex-col gap-1.5">
                        <label className="text-[9px] font-bold tracking-widest uppercase text-white/40">Your Name</label>
                        <input name="name" value={form.name} onChange={handleChange} placeholder="James Wilson"
                          className="w-full bg-white/8 border border-white/15 rounded px-3.5 py-2.5 text-sm text-white placeholder-white/25 focus:outline-none focus:border-gold/50 transition-colors" />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-[9px] font-bold tracking-widest uppercase text-white/40">Phone *</label>
                        <input name="phone" type="tel" required value={form.phone} onChange={handleChange} placeholder="(228) 000-0000"
                          className="w-full bg-white/8 border border-white/15 rounded px-3.5 py-2.5 text-sm text-white placeholder-white/25 focus:outline-none focus:border-gold/50 transition-colors" />
                      </div>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[9px] font-bold tracking-widest uppercase text-white/40">Email *</label>
                      <input name="email" type="email" required value={form.email} onChange={handleChange} placeholder="you@email.com"
                        className="w-full bg-white/8 border border-white/15 rounded px-3.5 py-2.5 text-sm text-white placeholder-white/25 focus:outline-none focus:border-gold/50 transition-colors" />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[9px] font-bold tracking-widest uppercase text-white/40">Service Needed</label>
                      <select name="service" value={form.service} onChange={handleChange}
                        className="w-full bg-white/8 border border-white/15 rounded px-3.5 py-2.5 text-sm text-white/70 focus:outline-none focus:border-gold/50 transition-colors appearance-none cursor-pointer">
                        <option value="" className="bg-neutral-900">Select a Service</option>
                        <option value="Roof Replacement" className="bg-neutral-900">Roof Replacement</option>
                        <option value="Roof Repair" className="bg-neutral-900">Roof Repair</option>
                        <option value="Storm Damage Restoration" className="bg-neutral-900">Storm Damage Restoration</option>
                        <option value="Insurance Claim Assistance" className="bg-neutral-900">Insurance Claim Assistance</option>
                        <option value="Commercial Roofing" className="bg-neutral-900">Commercial Roofing</option>
                        <option value="Financing" className="bg-neutral-900">Financing Available</option>
                      </select>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[9px] font-bold tracking-widest uppercase text-white/40">Property Address</label>
                      <input name="address" value={form.address} onChange={handleChange} placeholder="Gulfport, MS"
                        className="w-full bg-white/8 border border-white/15 rounded px-3.5 py-2.5 text-sm text-white placeholder-white/25 focus:outline-none focus:border-gold/50 transition-colors" />
                    </div>
                    {submit.status === 'error' && (
                      <p className="text-red-400 text-xs">{submit.message}</p>
                    )}
                    <button type="submit" disabled={submit.status === 'loading'}
                      className="bg-gold text-ink font-bold text-[11px] tracking-widest uppercase py-3.5 rounded hover:bg-gold-light transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 mt-1">
                      {submit.status === 'loading' ? 'Submitting...' : 'Submit Estimate Request →'}
                    </button>
                    <p className="text-center text-white/30 text-[10px] tracking-wider pb-1">
                      Or call: <a href="tel:2285473887" className="text-gold/70 hover:text-gold transition-colors cursor-pointer">(228) 547-3887</a>
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
